import React, { useEffect, useState } from 'react';
import { Tooltip } from 'syngenta-digital-cropwise-react-ui-kit';
import FieldIcon from 'components/Icons/FieldIcon';
import cleanGeometry from 'helpers/cleanGeometry';
import { Geometry } from '@turf/helpers';

const AdditionalFieldsBadge: React.FC<{
  count: number;
  color: string;
  tooltipOpen: boolean;
  isSelected: boolean;
  onOpen: () => void;
  onClose: () => void;
}> = ({ count, color, tooltipOpen, isSelected, onOpen, onClose }) => {
  const [hovered, setHovered] = useState(false);
  const highlighted = hovered || tooltipOpen;
  const highlightColor = isSelected ? '#AADAFF' : '#DFE2E7';
  return (
    <button
      type="button"
      className="text-sm font-normal flex-shrink-0 focus:outline-none focus:shadow-outline border-none bg-transparent p-0 cursor-pointer"
      style={{
        color,
        borderRadius: 2,
        padding: 4,
        margin: -4,
        background: highlighted ? highlightColor : undefined
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocus={() => setHovered(true)}
      onBlur={() => {
        setHovered(false);
        onClose();
      }}
      onKeyDown={(e: React.KeyboardEvent) => {
        e.stopPropagation();
      }}
      onClick={(e: React.MouseEvent) => {
        e.stopPropagation();
        if (tooltipOpen) onClose();
        else onOpen();
      }}
    >
      +{count}
    </button>
  );
};

interface FieldsDisplayProps {
  fields: Array<{
    id: string;
    name: string;
  }>;
  firstFieldInfo: {
    id?: string;
    name?: string;
    geometry?: Geometry;
  };
  totalFields: number;
  showSubheader?: boolean;
  iconWidth?: number;
  iconHeight?: number;
  iconColor?: string;
  iconBorderColor?: string;
  disablePositionOffset?: boolean;
  isSelected?: boolean;
}

const FieldsDisplay: React.FC<FieldsDisplayProps> = ({
  fields,
  firstFieldInfo,
  totalFields,
  showSubheader = false,
  iconWidth,
  iconHeight,
  iconColor = '#DFE2E7',
  iconBorderColor = '#868CA2',
  disablePositionOffset = false,
  isSelected = false
}) => {
  const [tooltipOpen, setTooltipOpen] = useState(false);

  // The Fields tooltip is mounted on document.body (see getPopupContainer)
  // so the scrollable table body can't clip it. The trade-off is that a
  // body-mounted popup doesn't ride along when the table scrolls, so close
  // it on any scroll to avoid it floating detached from the +N chip. Scroll
  // events don't bubble, hence the capture-phase listener — it still catches
  // the table body (or any ancestor) scrolling.
  useEffect(() => {
    if (!tooltipOpen) return undefined;
    const closeOnScroll = () => setTooltipOpen(false);
    window.addEventListener('scroll', closeOnScroll, true);
    return () => window.removeEventListener('scroll', closeOnScroll, true);
  }, [tooltipOpen]);

  const safeFields = fields || [];
  const firstField = safeFields[0];
  const safeTotalFields = totalFields || 0;
  const additionalFieldsCount = safeTotalFields - 1;

  if (!firstField) {
    return (
      <div className="text-left">
        {showSubheader && (
          <div className="text-xs font-normal" style={{ color: '#696F88' }}>
            Fields
          </div>
        )}
        <div className="text-sm text-gray-900">No fields</div>
      </div>
    );
  }

  const SUBTITLE_COLOR = '#696F88';
  const COUNT_COLOR = '#0071CD';
  const REMAINING_COLOR = '#DFE2E7';
  const TOOLTIP_BACKGROUND_COLOR = '#232630';

  // UI Layout Constants
  const FIELD_NAME_CHARS_PER_LINE = 28;
  const TOOLTIP_MAX_LINES = 15;

  const estimateFieldLines = (fieldName: string) => {
    const estimatedLines = Math.ceil(
      fieldName.length / FIELD_NAME_CHARS_PER_LINE
    );
    return Math.min(estimatedLines, 2);
  };

  const generateTooltipContent = () => {
    if (fields.length <= 1) {
      return null;
    }

    let currentLines = 0;
    const fieldsToShow: string[] = [];
    let remainingCount = 0;

    for (let i = 0; i < safeFields.length; i += 1) {
      const field = safeFields[i];
      // Calculate actual lines this field will likely use
      const fieldName = field?.name || '';
      const fieldLines = estimateFieldLines(fieldName);

      if (currentLines + fieldLines <= TOOLTIP_MAX_LINES) {
        fieldsToShow.push(fieldName);
        currentLines += fieldLines;
      } else {
        remainingCount = safeTotalFields - i;
        break;
      }
    }

    // Account for fields beyond what the API returned in firstFields
    if (remainingCount === 0 && safeTotalFields > fieldsToShow.length) {
      remainingCount = safeTotalFields - fieldsToShow.length;
    }

    return (
      <div className="text-left p-2">
        <div className="font-semibold mb-2">Fields</div>
        <ul className="list-disc pl-4 space-y-1 ml-1">
          {fieldsToShow.map(field => (
            <li key={field} className="text-sm">
              <div
                style={{
                  lineHeight: '1.4',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  wordBreak: 'break-all'
                }}
              >
                {field}
              </div>
            </li>
          ))}
        </ul>
        {remainingCount > 0 && (
          <div
            className="mt-2 text-sm font-normal"
            style={{ color: REMAINING_COLOR }}
          >
            + {remainingCount} more
          </div>
        )}
      </div>
    );
  };

  const tooltipContent = generateTooltipContent();

  const displayContent = (
    <div className="min-w-0">
      {/* Optional subheader */}
      {showSubheader && (
        <div className="text-xs font-normal" style={{ color: SUBTITLE_COLOR }}>
          Fields
        </div>
      )}
      {/* Field display */}
      <div
        className="flex items-center gap-2 min-w-0"
        style={
          disablePositionOffset
            ? undefined
            : { position: 'relative', top: '-7px' }
        }
      >
        <FieldIcon
          id={firstFieldInfo?.name || 'unknown'}
          className="flex-shrink-0"
          features={[
            {
              type: 'Feature',
              geometry: cleanGeometry(firstFieldInfo?.geometry) || {
                type: '',
                coordinates: []
              },
              properties: {}
            }
          ]}
          border={iconBorderColor}
          color={iconColor}
          width={iconWidth}
          height={iconHeight}
        />
        <div className="text-sm text-gray-900 truncate break-all flex-shrink min-w-0">
          {firstField?.name || 'No fields'}
        </div>

        {/* Additional fields count indicator. The Fields tooltip wraps the
            badge (not the whole row) so antd anchors to the chip itself.
            placement="topRight" keeps the popup right-aligned (per design),
            while arrowPointAtCenter makes the arrow point at the chip's
            center regardless of the +N width — so it doesn't drift off-center
            as the count grows (+9 → +9999), and it stays correct when the
            placement auto-flips up/down. A small horizontal-only align.offset
            corrects the residual lean (antd v4 clamps the arrow inset on a
            narrow trigger); it's a constant, not width-dependent, and being
            horizontal it survives the vertical flip. The badge is wrapped in a span so
            antd has a DOM node to attach its trigger/positioning to (the
            badge component isn't a ref forwarder, so hovering it otherwise
            wouldn't open the tooltip). */}
        {additionalFieldsCount > 0 &&
          (tooltipContent ? (
            <Tooltip
              title={tooltipContent}
              placement="topRight"
              arrowPointAtCenter
              align={{ offset: [24, 0] }}
              color={TOOLTIP_BACKGROUND_COLOR}
              open={tooltipOpen}
              onOpenChange={setTooltipOpen}
              // Mount on body (not the scrollable table cell) so the table's
              // overflow:auto can't clip the tooltip. Positioning is still
              // anchored to the badge, now with the viewport — not the scroll
              // container — as the auto-flip boundary.
              getPopupContainer={() => document.body}
            >
              <span className="inline-flex">
                <AdditionalFieldsBadge
                  count={additionalFieldsCount}
                  color={COUNT_COLOR}
                  tooltipOpen={tooltipOpen}
                  isSelected={isSelected}
                  onOpen={() => setTooltipOpen(true)}
                  onClose={() => setTooltipOpen(false)}
                />
              </span>
            </Tooltip>
          ) : (
            <AdditionalFieldsBadge
              count={additionalFieldsCount}
              color={COUNT_COLOR}
              tooltipOpen={tooltipOpen}
              isSelected={isSelected}
              onOpen={() => setTooltipOpen(true)}
              onClose={() => setTooltipOpen(false)}
            />
          ))}
      </div>
    </div>
  );

  return displayContent;
};

export default FieldsDisplay;
